import type { LandingTemplateButtonsMap, LandingTemplateEditDraft } from './landingTemplateButtons'
import {
  buildLandingTemplateFlowQuery,
  getLandingTemplateIdFromQuery,
  LANDING_BUTTON_MODE_MODULE,
  omitLandingTemplateFlowQuery,
  resolveLandingFlowEntityId,
  ROUTE_QUERY_LANDING_BUTTON_MODE,
  ROUTE_QUERY_LANDING_BUTTON_SLOT,
  ROUTE_QUERY_LANDING_EDIT_RETURN,
  ROUTE_QUERY_LANDING_ENTITY_ID,
  ROUTE_QUERY_LANDING_TEMPLATE_ID,
} from '~/shared/const/landingTemplateRoute'
import { permissions } from '~/shared/lib'
import { buildLandingTemplateDraftStorageKey } from './landingTemplateButtons'

export function useLandingTemplateEditDraft(templateNumericId?: string) {
  const route = useRoute()
  const router = useRouter()

  const entityId = computed(() => resolveLandingFlowEntityId(route.params, route.query))

  const resolvedTemplateId = computed(
    () => templateNumericId ?? getLandingTemplateIdFromQuery(route.query),
  )

  const storageKey = computed(() =>
    buildLandingTemplateDraftStorageKey(entityId.value, resolvedTemplateId.value),
  )

  function readDraft(): LandingTemplateEditDraft | null {
    if (!import.meta.client || !resolvedTemplateId.value) {
      return null
    }

    const raw = sessionStorage.getItem(storageKey.value)
    if (!raw) {
      return null
    }

    try {
      return JSON.parse(raw) as LandingTemplateEditDraft
    }
    catch {
      return null
    }
  }

  function writeDraft(draft: LandingTemplateEditDraft) {
    if (!import.meta.client || !resolvedTemplateId.value) {
      return
    }

    sessionStorage.setItem(storageKey.value, JSON.stringify(draft))
  }

  function clearDraft() {
    if (!import.meta.client || !resolvedTemplateId.value) {
      return
    }

    sessionStorage.removeItem(storageKey.value)
  }

  function navigateToModuleSelect(
    buttonSlot: string,
    editRoutePermission: string,
    draft: LandingTemplateEditDraft,
    buttonMode = LANDING_BUTTON_MODE_MODULE,
  ) {
    writeDraft(draft)

    const flowQuery = buildLandingTemplateFlowQuery(route.query, {
      [ROUTE_QUERY_LANDING_BUTTON_SLOT]: buttonSlot,
      [ROUTE_QUERY_LANDING_EDIT_RETURN]: editRoutePermission,
      [ROUTE_QUERY_LANDING_TEMPLATE_ID]: resolvedTemplateId.value,
      [ROUTE_QUERY_LANDING_BUTTON_MODE]: buttonMode,
      [ROUTE_QUERY_LANDING_ENTITY_ID]: entityId.value,
    })

    void router.push({
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.MODULE_SELECT.PERMISSION,
      params: { id: entityId.value },
      query: flowQuery,
    })
  }

  async function applyModuleSelection(
    buttonSlot: string,
    button: NonNullable<LandingTemplateButtonsMap[string]>,
    editRoutePermission: string,
    options?: {
      persistButtons?: (buttons: LandingTemplateButtonsMap) => Promise<void>
    },
  ) {
    const draft = readDraft()
    if (!draft) {
      return
    }

    draft.buttons = {
      ...draft.buttons,
      [buttonSlot]: button,
    }
    writeDraft(draft)

    if (options?.persistButtons) {
      await options.persistButtons(draft.buttons)
    }

    void router.replace({
      name: editRoutePermission,
      params: { id: entityId.value },
      query: omitLandingTemplateFlowQuery(route.query),
    })
  }

  return {
    entityId,
    readDraft,
    writeDraft,
    clearDraft,
    navigateToModuleSelect,
    applyModuleSelection,
  }
}
